"use client";

import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Loader2,
  RefreshCw,
  Save,
  TrendingUp,
} from "lucide-react";
import {
  PageHeader,
  Card,
  Spinner,
  useToast,
} from "@/components/ui";
import { Button } from "@/components/ui/ExamplePrimitives";
import { Sparkline } from "@/components/admin/ai-model-pricing/Sparkline";
import { StepByStepGuide } from "@/components/admin/ai-model-pricing/StepByStepGuide";
import {
  aiModelPricingApi,
  ModelStatusEntry,
  TierRecommendation,
} from "@/lib/aiModelPricing";

export default function AiModelPricingPage() {
  const { showToast } = useToast();
  const [status, setStatus] = useState<ModelStatusEntry[] | null>(null);
  const [recs, setRecs] = useState<TierRecommendation[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [probingId, setProbingId] = useState<string | null>(null);
  const [editingTier, setEditingTier] = useState<{ name: string; value: string } | null>(null);
  const [guideFor, setGuideFor] = useState<{ tier: string; open: boolean }>({
    tier: "",
    open: false,
  });

  const toastError = (msg: string) =>
    showToast({ variant: "error", title: msg });
  const toastSuccess = (msg: string) =>
    showToast({ variant: "success", title: msg });

  const refresh = async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([
        aiModelPricingApi.status(),
        aiModelPricingApi.recommendations(),
      ]);
      setStatus(s.models);
      setRecs(r.recommendations);
    } catch (e: any) {
      toastError(`Failed to load: ${e?.message ?? "unknown"}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const probeNow = async (modelId: string) => {
    setProbingId(modelId);
    try {
      await aiModelPricingApi.probeNow(modelId);
      toastSuccess("Probe complete");
      await refresh();
    } catch (e: any) {
      toastError(`Probe failed: ${e?.message ?? "unknown"}`);
    } finally {
      setProbingId(null);
    }
  };

  const saveMargin = async () => {
    if (!editingTier) return;
    try {
      await aiModelPricingApi.updateMargin(
        editingTier.name,
        Number(editingTier.value)
      );
      toastSuccess("Margin saved — recommendations refreshed");
      setEditingTier(null);
      await refresh();
    } catch (e: any) {
      toastError(`Save failed: ${e?.message ?? "unknown"}`);
    }
  };

  const stalenessWarning = useMemo(() => {
    const stale = (status ?? []).find((m) => (m.daysSinceLastProbe ?? 0) > 1);
    return stale
      ? `Last probe of ${stale.modelId} was ${stale.daysSinceLastProbe} days ago — daily cron is overdue.`
      : null;
  }, [status]);

  const formatUsd = (cents: number, currency: string) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency }).format(
      cents / 100
    );

  return (
    <div className="space-y-6">
      <PageHeader
        title="AI Model & Pricing"
        eyebrow="Gemini cost control"
        subtitle="Live Gemini model status, probe history, and margin-aware per-tier pricing."
      />

      {stalenessWarning && (
        <div className="flex items-start gap-3 rounded-xl border border-[#FFD60A]/30 bg-[#FFD60A]/10 px-4 py-3 text-sm text-[#FFD60A]">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{stalenessWarning}</span>
        </div>
      )}

      {/* ── Section A — Model cards ────────────────────────────────────── */}
      <section>
        <h3 className="mb-3 text-xs font-mono uppercase tracking-widest text-[var(--color-tertiary-label)]">
          Configured models
        </h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {loading && (
            <div className="col-span-2 flex items-center justify-center gap-2 py-6 text-[var(--color-tertiary-label)]">
              <Spinner className="!h-4 !w-4" />
              Loading model status…
            </div>
          )}
          {status?.map((m) => (
            <ModelCard
              key={m.modelId}
              model={m}
              probing={probingId === m.modelId}
              onProbe={() => probeNow(m.modelId)}
            />
          ))}
        </div>
      </section>

      {/* ── Section C — Recommendations ────────────────────────────── */}
      <section>
        <h3 className="mb-3 text-xs font-mono uppercase tracking-widest text-[var(--color-tertiary-label)]">
          Per-tier pricing recommendations
        </h3>
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] font-mono uppercase tracking-wider text-[var(--color-tertiary-label)]">
                  <th className="px-3 py-2">Tier</th>
                  <th className="px-3 py-2">Current</th>
                  <th className="px-3 py-2">Avg cost / user / mo</th>
                  <th className="px-3 py-2">Target margin</th>
                  <th className="px-3 py-2">Recommended</th>
                  <th className="px-3 py-2">Δ</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {recs?.map((r) => {
                  const isEditing = editingTier?.name === r.tierName;
                  return (
                    <tr
                      key={r.tierName}
                      className="border-t border-white/5"
                    >
                      <td className="px-3 py-2 font-mono font-bold">{r.tierName}</td>
                      <td className="px-3 py-2">
                        {formatUsd(r.currentPriceCents, r.currency)}
                      </td>
                      <td className="px-3 py-2 font-mono text-[var(--color-tertiary-label)]">
                        ${r.avgCostPerUserMonthUsd.toFixed(4)}
                      </td>
                      <td className="px-3 py-2 font-mono">
                        {isEditing ? (
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              value={editingTier.value}
                              onChange={(e) =>
                                setEditingTier({
                                  name: r.tierName,
                                  value: e.target.value,
                                })
                              }
                              className="w-20 rounded-md border border-white/10 bg-black/40 px-2 py-1 text-xs"
                            />
                            <span>%</span>
                          </div>
                        ) : (
                          `${r.targetMarginPercent.toFixed(0)}%`
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono font-bold text-[#FFD60A]">
                        {formatUsd(r.recommendedPriceCents, r.currency)}
                      </td>
                      <td className="px-3 py-2 font-mono">
                        <span
                          className={
                            r.deltaCents > 0
                              ? "text-[#FF3377]"
                              : r.deltaCents < 0
                              ? "text-emerald-400"
                              : "text-[var(--color-tertiary-label)]"
                          }
                        >
                          {r.deltaCents > 0 ? "+" : ""}
                          {formatUsd(r.deltaCents, r.currency)}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right space-x-2">
                        {isEditing ? (
                          <>
                            <Button onClick={saveMargin} size="sm">
                              <Save className="h-3 w-3" />
                              Save
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditingTier(null)}
                            >
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                setEditingTier({
                                  name: r.tierName,
                                  value: r.targetMarginPercent.toString(),
                                })
                              }
                            >
                              Edit margin
                            </Button>
                            <Button
                              size="sm"
                              onClick={() =>
                                setGuideFor({ tier: r.tierName, open: true })
                              }
                            >
                              View steps
                            </Button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {!loading && (recs?.length ?? 0) === 0 && (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-3 py-6 text-center text-[var(--color-tertiary-label)]"
                    >
                      No tiers configured yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="mt-3 font-mono text-[11px] text-[var(--color-tertiary-label)]">
            Avg cost = mean of (sum of{" "}
            <code className="text-[#FFD60A]">ai_image_logs</code> grouped by user)
            over the last 30 days. Recommended price = ceil(avg_cost / (1 −
            target_margin/100)).
          </div>
        </Card>
      </section>

      <div className="text-[11px] text-[var(--color-tertiary-label)]">
        Edit{" "}
        <code className="text-white">
          docs_group_B/gemini-model-reference.json
        </code>{" "}
        to push <code className="text-white">availableUntil</code> updates — the
        next probe picks them up automatically.
      </div>

      <StepByStepGuide
        tier={guideFor.tier}
        open={guideFor.open}
        onClose={() => setGuideFor((g) => ({ ...g, open: false }))}
      />
    </div>
  );
}

function ModelCard({
  model,
  probing,
  onProbe,
}: {
  model: ModelStatusEntry;
  probing: boolean;
  onProbe: () => void;
}) {
  const [history, setHistory] = useState<
    { probedAt: string; ok: boolean; latencyMs: number }[]
  >([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoadingHistory(true);
    aiModelPricingApi
      .history(model.modelId, 30)
      .then((rows) => {
        if (!cancelled) setHistory(rows);
      })
      .catch(() => {
        if (!cancelled) setHistory([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingHistory(false);
      });
    return () => {
      cancelled = true;
    };
  }, [model.modelId]);

  const badgeClass =
    model.status === "live"
      ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
      : model.status === "degraded"
      ? "bg-[#FFD60A]/15 text-[#FFD60A] border-[#FFD60A]/30"
      : "bg-[#FF3377]/15 text-[#FF3377] border-[#FF3377]/30";

  return (
    <Card>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="truncate font-mono text-sm font-bold">
            {model.modelId}
          </div>
          <div className="mt-1 text-[10px] font-mono uppercase tracking-wider text-[var(--color-tertiary-label)]">
            {model.category === "text"
              ? "Text"
              : model.category === "image"
              ? "Image"
              : "—"}
            {model.currentPricePerMTokensUsd != null &&
              ` · $${model.currentPricePerMTokensUsd}/1M in`}
            {model.currentPricePerImageUsd != null &&
              ` · $${model.currentPricePerImageUsd}/img`}
          </div>
        </div>
        <span
          className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${badgeClass}`}
        >
          {model.status === "live" ? (
            <CheckCircle2 className="h-3 w-3" />
          ) : model.status === "degraded" ? (
            <Clock className="h-3 w-3" />
          ) : (
            <AlertTriangle className="h-3 w-3" />
          )}
          {model.status}
        </span>
      </div>

      <div className="mt-4">
        {loadingHistory ? (
          <div className="flex h-16 items-center justify-center text-[var(--color-tertiary-label)]">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        ) : (
          <Sparkline history={history} height={64} />
        )}
      </div>

      <div className="mt-4 flex items-center justify-between">
        <div className="font-mono text-[11px] text-[var(--color-tertiary-label)]">
          {model.lastProbedAt
            ? `Last probe ${model.daysSinceLastProbe ?? 0}d ago · ${
                model.lastLatencyMs ?? "?"
              } ms`
            : "Never probed yet"}
          {model.availableUntil && (
            <div className="mt-1 text-[#FFD60A]">
              <TrendingUp className="mr-1 inline h-3 w-3" />
              available until {model.availableUntil}
              {model.replacement && (
                <span className="text-[var(--color-tertiary-label)]">
                  {" → "}
                  {model.replacement}
                </span>
              )}
            </div>
          )}
        </div>
        <Button variant="ghost" size="sm" onClick={onProbe} disabled={probing}>
          {probing ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <RefreshCw className="h-3 w-3" />
          )}
          Refresh
        </Button>
      </div>
    </Card>
  );
}
